(function () {
  "use strict";

  if (customElements.get("bundle-tabbed-slider")) return;

  class BundleTabbedSlider extends HTMLElement {
    connectedCallback() {
      const root = this;

      this.triggers = Array.from(root.querySelectorAll("[data-tab-trigger]"));
      this.panels = Array.from(root.querySelectorAll("[data-tab-panel]"));
      this.markers = Array.from(root.querySelectorAll("[data-tier-marker]"));
      this.tierFillEl = root.querySelector("[data-tier-fill]");
      this.countEl = root.querySelector("[data-bundle-count]");
      this.listEl = root.querySelector("[data-bundle-list]");
      this.emptyEl = root.querySelector("[data-panel-empty]");
      this.emptyTextEl = root.querySelector("[data-panel-empty-text]");
      this.addBtn = root.querySelector("[data-bundle-add-btn]");
      this.errorEl = root.querySelector("[data-bundle-error]");
      this.subtotalEl = root.querySelector("[data-bundle-subtotal]");
      this.discountRow = root.querySelector("[data-discount-row]");
      this.discountRowLabel = root.querySelector("[data-discount-row-label]");
      this.discountAmountEl = root.querySelector("[data-bundle-discount-amount]");
      this.totalEl = root.querySelector("[data-bundle-total]");
      this.capacityNoteEl = root.querySelector("[data-capacity-note]");

      // Tiers, sorted ascending by size.
      this.tiers = this.markers
        .map((m) => ({ size: Number(m.dataset.size), percentage: Number(m.dataset.percentage) }))
        .sort((a, b) => a.size - b.size);

      this.minTierSize = this.tiers.length ? this.tiers[0].size : 1;
      this.maxTierSize = this.tiers.length ? this.tiers[this.tiers.length - 1].size : 1;
      this.maxPerProduct = parseInt(root.dataset.maxPerProduct, 10) || 1;
      this.cartLimit = parseInt(root.dataset.cartLimit, 10) || null;

      this.markers.forEach((m) => {
        const size = Number(m.dataset.size);
        m.style.left = (size / this.maxTierSize) * 100 + "%";
      });

      // Product ids eligible toward the cart-capacity check, gathered from
      // every tab -- including tabs whose cards are still sitting inside an
      // un-hydrated <template> and haven't been opened yet.
      this.eligibleProductIds = this.collectEligibleProductIds();

      this.alreadyInCartQty = 0;
      this.remainingCapacity = this.cartLimit; // null = unknown/no limit configured

      // Quantity per card element, e.g. Map<cardEl, 1|2>. Cards not in the map = 0.
      this.quantities = new Map();

      // Wire whatever cards already exist in the live DOM (the first/active tab).
      this.getCards().forEach((card) => this.wireCard(card));

      this.triggers.forEach((trigger) => {
        trigger.addEventListener("click", () => this.activateTab(trigger.dataset.tabIndex));
      });

      this.addBtn.addEventListener("click", () => this.addBundleToCart());

      document.addEventListener("cart:refresh", () => this.refreshCartCapacity());

      this.statusHintEl = this.createStatusHintEl();

      this.refreshCartCapacity();
      this.render();
    }

    collectEligibleProductIds() {
      const ids = new Set();
      this.getCards().forEach((card) => ids.add(card.dataset.productId));
      this.querySelectorAll("template[data-tab-template]").forEach((template) => {
        template.content.querySelectorAll("[data-bundle-card]").forEach((card) => {
          ids.add(card.dataset.productId);
        });
      });
      return ids;
    }

    getCards() {
      return Array.from(this.querySelectorAll("[data-bundle-card]"));
    }

    wireCard(card) {
      if (card.dataset.wired === "true") return;
      card.dataset.wired = "true";

      const addBtnEl = card.querySelector("[data-card-add]");
      const minus = card.querySelector("[data-stepper-minus]");
      const plus = card.querySelector("[data-stepper-plus]");

      addBtnEl.addEventListener("click", () => this.changeQuantity(card, 1));
      minus.addEventListener("click", () => this.changeQuantity(card, -1));
      plus.addEventListener("click", () => this.changeQuantity(card, 1));
    }

    // --- Tabs ---

    activateTab(index) {
      this.triggers.forEach((trigger) => {
        const isActive = trigger.dataset.tabIndex === index;
        trigger.classList.toggle("is-active", isActive);
        trigger.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.tabIndex === index;
        panel.classList.toggle("is-active", isActive);
        panel.hidden = !isActive;
        if (isActive) this.hydrate(panel);
      });

      // A newly hydrated tab may reveal cards whose add/stepper state
      // (disabled, quantity, etc.) needs to reflect the current selection.
      this.render();
    }

    // Moves a panel's <template> contents into the live DOM the first time its
    // tab is activated, so slider-component only connects (and Dawn's slider
    // JS only initializes) for tabs the shopper has actually opened. Newly
    // revealed cards are wired for add/remove immediately after.
    hydrate(panel) {
      const template = panel.querySelector("[data-tab-template]");
      if (!template) return;
      panel.appendChild(template.content.cloneNode(true));
      template.remove();
      panel.querySelectorAll("[data-bundle-card]").forEach((card) => this.wireCard(card));
    }

    // --- Cart capacity (soft, display-only; the real cap is enforced server-side) ---

    async refreshCartCapacity() {
      if (!this.cartLimit) return;
      try {
        const res = await fetch(window.Shopify.routes.root + "cart.js");
        const cart = await res.json();
        this.alreadyInCartQty = cart.items
          .filter((item) => this.eligibleProductIds.has(String(item.product_id)))
          .reduce((sum, item) => sum + item.quantity, 0);
        this.remainingCapacity = Math.max(0, this.cartLimit - this.alreadyInCartQty);
        this.renderCapacityNote();
        this.render();
      } catch {
        // If the cart can't be read, skip the soft check silently -- the
        // cart-limit validation function still enforces the real cap regardless.
      }
    }

    renderCapacityNote() {
      if (!this.cartLimit || !this.capacityNoteEl) return;
      if (this.alreadyInCartQty === 0) {
        this.capacityNoteEl.hidden = true;
        return;
      }
      this.capacityNoteEl.hidden = false;
      const limitReached = this.remainingCapacity <= 0;
      this.capacityNoteEl.dataset.limitReached = String(limitReached);
      this.capacityNoteEl.textContent = limitReached
        ? `You've reached the limit of ${this.cartLimit} items from these collections in one order.`
        : `You already have ${this.alreadyInCartQty} item${this.alreadyInCartQty === 1 ? "" : "s"} from these collections in your cart (${this.remainingCapacity} remaining out of ${this.cartLimit}).`;
    }

    // --- Selection state ---

    totalSelected() {
      let sum = 0;
      this.quantities.forEach((qty) => (sum += qty));
      return sum;
    }

    effectiveCap() {
      if (this.remainingCapacity == null) return this.maxTierSize;
      return Math.min(this.maxTierSize, this.remainingCapacity);
    }

    currentTier() {
      const total = this.totalSelected();
      const qualifying = this.tiers.filter((t) => total >= t.size);
      return qualifying.length ? qualifying[qualifying.length - 1] : null;
    }

    nextTier() {
      const total = this.totalSelected();
      return this.tiers.find((t) => t.size > total) || null;
    }

    changeQuantity(card, delta) {
      const current = this.quantities.get(card) || 0;
      const next = current + delta;

      if (next < 0) return;
      if (next > this.maxPerProduct) return;
      if (delta > 0 && this.totalSelected() >= this.effectiveCap()) return; // overall ceiling

      if (next === 0) {
        this.quantities.delete(card);
      } else {
        this.quantities.set(card, next);
      }
      this.render();
    }

    // --- Rendering ---

    render() {
      const cap = this.effectiveCap();
      const total = this.totalSelected();
      const tier = this.currentTier();
      const next = this.nextTier();

      this.getCards().forEach((card) => {
        const qty = this.quantities.get(card) || 0;
        const addBtnEl = card.querySelector("[data-card-add]");
        const stepperEl = card.querySelector("[data-card-stepper]");
        const qtyEl = card.querySelector("[data-stepper-qty]");
        const minusBtn = card.querySelector("[data-stepper-minus]");
        const plusBtn = card.querySelector("[data-stepper-plus]");

        addBtnEl.hidden = qty > 0;
        stepperEl.hidden = qty === 0;
        addBtnEl.disabled = total >= cap;

        qtyEl.textContent = String(qty);
        card.classList.toggle("is-selected", qty > 0);
        minusBtn.disabled = qty === 0;
        plusBtn.disabled = qty >= this.maxPerProduct || total >= cap;
      });

      this.countEl.textContent = `${total} selected`;

      const fillPct = Math.min(100, (total / this.maxTierSize) * 100);
      this.tierFillEl.style.width = fillPct + "%";
      this.markers.forEach((m) => {
        const size = Number(m.dataset.size);
        m.classList.toggle("is-reached", total >= size);
        m.classList.toggle("is-active", tier && tier.size === size);
      });

      this.listEl.querySelectorAll("[data-panel-item]").forEach((el) => el.remove());
      this.emptyEl.hidden = total > 0;
      if (this.emptyTextEl) {
        this.emptyTextEl.textContent = `Pick at least ${this.minTierSize} products (from any tab) to unlock your discount, up to ${this.maxPerProduct} of the same item.`;
      }

      this.quantities.forEach((qty, card) => {
        const li = document.createElement("li");
        li.className = "bb-tabbed__panel-item";
        li.dataset.panelItem = "";
        li.innerHTML = `
          <img class="bb-tabbed__panel-item-image" src="${card.dataset.image}" alt="" width="36" height="36">
          <span class="bb-tabbed__panel-item-title">${card.dataset.title}${qty > 1 ? ` <span class="bb-tabbed__panel-item-qty">×${qty}</span>` : ""}</span>
          <button type="button" class="bb-tabbed__panel-item-remove" aria-label="Remove ${card.dataset.title} from bundle">
            <span aria-hidden="true">&times;</span>
          </button>
        `;
        li.querySelector(".bb-tabbed__panel-item-remove").addEventListener("click", () => {
          this.quantities.delete(card);
          this.render();
        });
        this.listEl.appendChild(li);
      });

      this.updateTotals(tier);

      this.addBtn.disabled = total < this.minTierSize;
      this.addBtn.textContent = tier
        ? `Add ${total} to cart (${tier.percentage}% off)`
        : total > 0
          ? `Add ${this.minTierSize - total} more to unlock a discount`
          : "Add bundle to cart";

      if (this.statusHintEl) {
        if (next && tier) {
          this.statusHintEl.textContent = `Add ${next.size - total} more to reach ${next.percentage}% off.`;
          this.statusHintEl.hidden = false;
        } else {
          this.statusHintEl.hidden = true;
        }
      }

      this.errorEl.hidden = true;
    }

    updateTotals(tier) {
      let subtotalCents = 0;
      this.quantities.forEach((qty, card) => {
        subtotalCents += parseInt(card.dataset.price, 10) * qty;
      });

      if (subtotalCents === 0) {
        this.subtotalEl.textContent = "—";
        this.totalEl.textContent = "—";
        this.discountRow.hidden = true;
        return;
      }

      this.subtotalEl.textContent = this.formatMoney(subtotalCents);

      if (tier) {
        const discountCents = Math.round(subtotalCents * (tier.percentage / 100));
        const totalCents = subtotalCents - discountCents;
        this.discountRow.hidden = false;
        if (this.discountRowLabel) this.discountRowLabel.textContent = `Bundle discount (${tier.percentage}%)`;
        this.discountAmountEl.textContent = `-${this.formatMoney(discountCents)}`;
        this.totalEl.textContent = this.formatMoney(totalCents);
      } else {
        this.discountRow.hidden = true;
        this.totalEl.textContent = this.formatMoney(subtotalCents);
      }
    }

    formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
      }
      return (cents / 100).toLocaleString(undefined, {
        style: "currency",
        currency: window.Shopify?.currency?.active || "USD",
      });
    }

    async addBundleToCart() {
      const total = this.totalSelected();
      if (total < this.minTierSize || this.addBtn.disabled) return;

      this.addBtn.disabled = true;
      const originalLabel = this.addBtn.textContent;
      this.addBtn.textContent = "Adding…";
      this.errorEl.hidden = true;

      const items = [];
      this.quantities.forEach((qty, card) => {
        items.push({
          id: parseInt(card.dataset.variantId, 10),
          quantity: qty,
          properties: {
            _bundlepack: total + "-pack",
            _bundle: "bundle",
          },
        });
      });

      try {
        const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.description || "Couldn't add the bundle to your cart.");
        }

        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        if (typeof slideShiprocketSmartCartInFrame === "function") {
          setTimeout(() => slideShiprocketSmartCartInFrame(), 1500);
        }
        this.addBtn.textContent = "Added ✓";
        setTimeout(() => {
          this.quantities.clear();
          this.render();
        }, 1200);
      } catch (err) {
        this.errorEl.textContent = err.message || "Something went wrong. Please try again.";
        this.errorEl.hidden = false;
        this.addBtn.disabled = false;
        this.addBtn.textContent = originalLabel;
      }
    }

    createStatusHintEl() {
      const el = document.createElement("p");
      el.className = "bb-tabbed__status-hint";
      el.hidden = true;
      this.querySelector(".bb-tabbed__panel-head").insertAdjacentElement("afterend", el);
      return el;
    }
  }

  customElements.define("bundle-tabbed-slider", BundleTabbedSlider);
})();
